import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Upload, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, CITIES } from "./constants";

export function BlogPostForm({ post, onSave, onCancel, password }) {
  const [titleRu, setTitleRu] = useState(post?.title_ru || "");
  const [titleEn, setTitleEn] = useState(post?.title_en || "");
  const [slug, setSlug] = useState(post?.slug || "");
  const [contentRu, setContentRu] = useState(post?.content_ru || "");
  const [contentEn, setContentEn] = useState(post?.content_en || "");
  const [excerptRu, setExcerptRu] = useState(post?.excerpt_ru || "");
  const [excerptEn, setExcerptEn] = useState(post?.excerpt_en || "");
  const [metaRu, setMetaRu] = useState(post?.meta_description_ru || "");
  const [metaEn, setMetaEn] = useState(post?.meta_description_en || "");
  const [city, setCity] = useState(post?.city || "");
  const [tagsStr, setTagsStr] = useState((post?.tags || []).join(", "));
  const [coverImage, setCoverImage] = useState(post?.cover_image || "");
  const [coverFile, setCoverFile] = useState(null);
  const [status, setStatus] = useState(post?.status || "draft");
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("ru");

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverFile(file);
    const formData = new FormData();
    formData.append("password", password);
    formData.append("image", file);
    try {
      const res = await axios.post(`${API}/admin/articles/upload-image`, formData);
      setCoverImage(res.data.url);
      toast.success("Обложка загружена");
    } catch {
      toast.error("Ошибка загрузки обложки");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!titleRu) return toast.error("Заполните заголовок (RU)");
    if (!contentRu) return toast.error("Заполните контент (RU)");

    setSaving(true);
    try {
      const tags = tagsStr.split(",").map(t => t.trim()).filter(Boolean);
      const payload = {
        password,
        title_ru: titleRu,
        title_en: titleEn,
        slug,
        content_ru: contentRu,
        content_en: contentEn,
        excerpt_ru: excerptRu,
        excerpt_en: excerptEn,
        meta_description_ru: metaRu,
        meta_description_en: metaEn,
        city,
        tags,
        cover_image: coverImage,
        status,
      };

      if (post?.id) {
        await axios.put(`${API}/admin/articles/${post.id}`, payload);
        toast.success("Статья обновлена");
      } else {
        await axios.post(`${API}/admin/articles`, payload);
        toast.success("Статья создана");
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-pink-400 focus:ring-2 focus:ring-pink-100 outline-none text-sm";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="blog-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10 rounded-t-2xl">
          <h2 className="text-lg font-bold">{post?.id ? "Редактировать статью" : "Новая статья"}</h2>
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {/* Cover image */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Обложка</label>
            {coverImage ? (
              <div className="relative w-full aspect-[16/7] rounded-xl overflow-hidden bg-gray-100 mb-2">
                <img src={coverImage} alt="Cover" className="w-full h-full object-cover" />
                <button type="button" onClick={() => { setCoverImage(""); setCoverFile(null); }} className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-32 rounded-xl border-2 border-dashed border-gray-300 hover:border-pink-400 cursor-pointer bg-gray-50 transition-colors">
                <Upload className="w-6 h-6 text-gray-400 mb-1" />
                <span className="text-sm text-gray-500">Загрузить обложку</span>
                <input type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" data-testid="blog-cover-upload" />
              </label>
            )}
          </div>

          {/* Language tabs */}
          <div className="flex gap-2 border-b border-gray-100 pb-1">
            <button type="button" onClick={() => setActiveTab("ru")} className={`px-4 py-2 text-sm font-bold rounded-t-lg transition-colors ${activeTab === "ru" ? "text-pink-600 border-b-2 border-pink-500" : "text-gray-500"}`}>
              Русский
            </button>
            <button type="button" onClick={() => setActiveTab("en")} className={`px-4 py-2 text-sm font-bold rounded-t-lg transition-colors ${activeTab === "en" ? "text-pink-600 border-b-2 border-pink-500" : "text-gray-500"}`}>
              English
            </button>
          </div>

          {/* RU content */}
          {activeTab === "ru" && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Заголовок (RU) *</label>
                <input type="text" value={titleRu} onChange={e => setTitleRu(e.target.value)} placeholder="Доставка цветов в Анталье..." className={inputCls} data-testid="blog-title-ru" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Краткое описание (RU)</label>
                <textarea value={excerptRu} onChange={e => setExcerptRu(e.target.value)} placeholder="Краткое описание для списка..." rows={2} className={inputCls} data-testid="blog-excerpt-ru" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Контент (RU, Markdown) *</label>
                <textarea value={contentRu} onChange={e => setContentRu(e.target.value)} placeholder="# Заголовок&#10;&#10;Текст статьи..." rows={12} className={`${inputCls} font-mono text-xs`} data-testid="blog-content-ru" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Meta Description (RU)</label>
                <input type="text" value={metaRu} onChange={e => setMetaRu(e.target.value)} placeholder="SEO описание для Google..." className={inputCls} data-testid="blog-meta-ru" />
              </div>
            </div>
          )}

          {/* EN content */}
          {activeTab === "en" && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Title (EN)</label>
                <input type="text" value={titleEn} onChange={e => setTitleEn(e.target.value)} placeholder="Flower delivery in Antalya..." className={inputCls} data-testid="blog-title-en" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Excerpt (EN)</label>
                <textarea value={excerptEn} onChange={e => setExcerptEn(e.target.value)} placeholder="Short description..." rows={2} className={inputCls} data-testid="blog-excerpt-en" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Content (EN, Markdown)</label>
                <textarea value={contentEn} onChange={e => setContentEn(e.target.value)} placeholder="# Title&#10;&#10;Article text..." rows={12} className={`${inputCls} font-mono text-xs`} data-testid="blog-content-en" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Meta Description (EN)</label>
                <input type="text" value={metaEn} onChange={e => setMetaEn(e.target.value)} placeholder="SEO description for Google..." className={inputCls} data-testid="blog-meta-en" />
              </div>
            </div>
          )}

          {/* Slug + City + Tags */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Slug (URL)</label>
              <input type="text" value={slug} onChange={e => setSlug(e.target.value)} placeholder="auto-generated-if-empty" className={inputCls} data-testid="blog-slug" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Город</label>
              <select value={city} onChange={e => setCity(e.target.value)} className={inputCls} data-testid="blog-city-select">
                <option value="">Общая (без города)</option>
                {CITIES.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Теги (через запятую)</label>
            <input type="text" value={tagsStr} onChange={e => setTagsStr(e.target.value)} placeholder="розы, доставка, свадьба" className={inputCls} data-testid="blog-tags" />
          </div>

          {/* Status + Submit */}
          <div className="flex items-center gap-4 pt-2">
            <select value={status} onChange={e => setStatus(e.target.value)} className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium" data-testid="blog-status-select">
              <option value="draft">Черновик</option>
              <option value="published">Опубликовано</option>
            </select>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 text-white h-12 rounded-xl font-bold flex items-center justify-center gap-2"
              data-testid="blog-save-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? "Сохранение..." : post?.id ? "Сохранить" : "Создать статью"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
